import type { FitAddon } from '@xterm/addon-fit';
import type { ITheme, Terminal } from '@xterm/xterm';
import { LEGACY, TOKENS } from '@orgus/tokens';
import '@xterm/xterm/css/xterm.css';
import { Eye, Hand, Keyboard, LogOut, Users } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { focusOutside } from '../lib/focus.ts';
import { formatDuration } from '../lib/format.ts';
import type { Tone } from '../lib/status.ts';
import { useNow } from '../lib/useNow.ts';
import { Button } from './Button.tsx';
import { ErrorState } from './ErrorState.tsx';
import { Kbd } from './Kbd.tsx';
import { REVEAL_GROUP, RevealSlot } from './Reveal.tsx';
import { Skeleton } from './Skeleton.tsx';
import { SCROLL_EDGE_FADE, useScrollEdges } from './Tabs.tsx';
import { StatusDot } from './StatusDot.tsx';
import { Tooltip } from './Tooltip.tsx';

/** Where a pane's bytes come from and where its keystrokes go (a WebSocket, or demo data). */
export interface TerminalSource {
  /** Output for the screen. `reset`: clear the screen first (a replay of the whole screen). Returns unsubscribe. */
  subscribe(onData: (data: string, reset: boolean) => void): () => void;
  /** Keystrokes, sent only while you have control. */
  input(data: string): void;
  /** Your window's size, sent only while you have control (the writer's size wins). */
  resize(cols: number, rows: number): void;
}

export type TerminalPaneState = 'connecting' | 'live' | 'disconnected' | 'ended' | 'error';

export interface TerminalPaneStatus {
  /** What runs in it: "Claude Code" or "Shell". */
  agent: string;
  model?: string | null;
  machine?: string;
  startedAt?: number;
  endedAt?: number | null;
  /** Preformatted, for example "201k in / 22k out". */
  tokens?: string;
  cost?: string;
  viewers?: number;
  /** 'pipe': no PTY on that machine, so no full-screen programs. */
  backend?: 'pty' | 'pipe';
}

export interface TerminalPaneProps {
  /** Accessible name, for example "Terminal: fix the flaky test". */
  label: string;
  title: ReactNode;
  source: TerminalSource | null;
  state: TerminalPaneState;
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  /** The terminal's size on the machine. Read-only panes show exactly this. */
  cols: number;
  rows: number;
  /** You have control: keystrokes go to the machine. Read-only otherwise. */
  writable: boolean;
  /** Who is typing (null: nobody). */
  lock: { label: string; you: boolean } | null;
  status: TerminalPaneStatus;
  exitCode?: number | null;
  /** Take control. Absent: you may only watch. */
  onTakeControl?: () => void;
  onHandBack?: () => void;
  /** Take control from the person typing (admins). */
  onForce?: () => void;
  /** A control request is in flight. */
  busy?: boolean;
  /** Extra header actions (open full size, close). */
  actions?: ReactNode;
  /** Split view: a shorter header. */
  compact?: boolean;
  /** The machine runs Windows (ConPTY): tells xterm how that PTY reflows on resize. */
  windowsPty?: boolean;
  /** The title's heading level: 2 on a page of terminals (right under the h1), 3 inside a drawer. */
  headingLevel?: 2 | 3;
  className?: string;
}

const STATE_TONE: Record<TerminalPaneState, Tone> = {
  connecting: 'queued',
  live: 'working',
  disconnected: 'offline',
  ended: 'ended',
  error: 'failed',
};

function stateLabel(state: TerminalPaneState, exitCode: number | null | undefined): string {
  if (state === 'ended') return exitCode == null ? 'Ended' : `Ended, exit ${exitCode}`;
  return { connecting: 'Connecting', live: 'Live', disconnected: 'Machine offline', error: 'Not connected' }[state];
}

/** Dark-theme token values by CSS name (legacy names resolved), for when the page has no CSS yet. */
const DARK_FALLBACK: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const t of Object.values(TOKENS) as { css: string; value: unknown }[]) {
    const v = t.value;
    if (typeof v === 'string') out[t.css] = v;
    else if (v && typeof v === 'object' && 'dark' in v) out[t.css] = String((v as { dark: unknown }).dark);
  }
  for (const [legacy, current] of Object.entries(LEGACY) as [string, string][]) if (out[current]) out[legacy] = out[current];
  return out;
})();

/** The xterm theme from the design tokens (read at runtime, so light and dark both work). */
function tokenTheme(el: HTMLElement): ITheme {
  const css = getComputedStyle(el);
  const v = (name: string) => css.getPropertyValue(name).trim() || DARK_FALLBACK[name] || '';
  const fg = v('--fg');
  return {
    background: v('--bg'),
    foreground: fg,
    cursor: v('--accent'),
    cursorAccent: v('--bg'),
    selectionBackground: v('--accent-soft'),
    black: v('--surface-raised'),
    red: v('--st-failed'),
    green: v('--st-passed'),
    yellow: v('--st-waiting'),
    blue: v('--accent'),
    magenta: v('--hue-5'),
    cyan: v('--hue-4'),
    white: v('--fg-muted'),
    brightBlack: v('--fg-faint'),
    brightRed: v('--st-failed'),
    brightGreen: v('--st-passed'),
    brightYellow: v('--st-waiting'),
    brightBlue: v('--accent-hover'),
    brightMagenta: v('--hue-5'),
    brightCyan: v('--hue-4'),
    brightWhite: fg,
  };
}

function Elapsed({ from, to }: { from: number; to?: number | null }) {
  const now = useNow(1000);
  return <span className="num">{formatDuration(Math.max(0, (to ?? now) - from))}</span>;
}

/**
 * A live terminal (xterm.js): read-only by default, "Take control" to type, exactly one
 * writer at a time. The status bar says what runs in it, where, with which model, for how long.
 */
export function TerminalPane({
  label,
  title,
  source,
  state,
  error,
  onRetry,
  cols,
  rows,
  writable,
  lock,
  status,
  exitCode,
  onTakeControl,
  onHandBack,
  onForce,
  busy,
  actions,
  compact,
  windowsPty,
  headingLevel = 3,
  className,
}: TerminalPaneProps) {
  const host = useRef<HTMLDivElement>(null);
  // A watched screen wider than the pane fades at the edge that hides more of it.
  useScrollEdges(host);
  const term = useRef<Terminal | null>(null);
  const fit = useRef<FitAddon | null>(null);
  const writableRef = useRef(writable);
  const sourceRef = useRef(source);
  const gotOutput = useRef(false);
  const baseFont = compact ? 12 : 13;
  const hintId = useId();
  // xterm.js loads on first use, so pages without a terminal do not carry it.
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  writableRef.current = writable;
  sourceRef.current = source;

  // One xterm per pane.
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let disposed = false;
    let cleanup: () => void = () => {};
    void Promise.all([import('@xterm/xterm'), import('@xterm/addon-fit')]).then(([{ Terminal: XTerm }, { FitAddon: Fit }]) => {
      if (disposed) return;
      const css = getComputedStyle(el);
      const t = new XTerm({
        fontFamily: css.getPropertyValue('--font-mono').trim() || 'monospace',
        fontSize: baseFont,
        lineHeight: 1.15,
        cursorBlink: false,
        disableStdin: true,
        scrollback: 5000,
        allowTransparency: false,
        theme: tokenTheme(el),
        cols,
        rows,
        ...(windowsPty ? { windowsPty: { backend: 'conpty' as const, buildNumber: 22000 } } : {}),
      });
      const f = new Fit();
      t.loadAddon(f);
      t.open(el);
      term.current = t;
      fit.current = f;
      // Keyboard way out (WCAG 2.1.2, no keyboard trap). Watching, the terminal takes no keys,
      // so Tab moves on as anywhere else. While you type, Tab belongs to the program (shell
      // completion), so F6 and Shift+F6 leave the terminal instead; the textarea's description
      // says so.
      t.attachCustomKeyEventHandler((e) => {
        if (e.key === 'F6' && !e.ctrlKey && !e.altKey && !e.metaKey) {
          if (e.type === 'keydown') {
            e.preventDefault();
            focusOutside(el, e.shiftKey ? -1 : 1);
          }
          return false;
        }
        if (e.key === 'Tab' && !writableRef.current) return false;
        return true;
      });
      t.textarea?.setAttribute('aria-describedby', hintId);
      const typed = t.onData((d) => {
        if (writableRef.current) sourceRef.current?.input(d);
      });
      // Follow the theme (light, dark, high contrast) when the root element changes.
      const mo = new MutationObserver(() => {
        t.options.theme = tokenTheme(el);
      });
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
      setReady(true);
      cleanup = () => {
        typed.dispose();
        mo.disconnect();
        t.dispose();
        term.current = null;
        fit.current = null;
      };
    }, () => {
      if (!disposed) setLoadFailed(true);
    });
    return () => {
      disposed = true;
      cleanup();
    };
    // The terminal is created once; size and mode changes are applied below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While you type, Esc belongs to the program in the terminal (it stops Claude Code), not to
  // the drawer or dialog around the pane: take it before they see it and send it on.
  useEffect(() => {
    if (!writable) return;
    const onKey = (e: KeyboardEvent) => {
      const el = host.current;
      if (e.key !== 'Escape' || !el || !(e.target instanceof Node) || !el.contains(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      sourceRef.current?.input('\u001b');
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [writable]);

  // Output.
  useEffect(() => {
    const t = term.current;
    if (!t || !source) return;
    gotOutput.current = false;
    return source.subscribe((data, reset) => {
      if (reset) t.reset();
      if (data) {
        gotOutput.current = true;
        t.write(data);
      }
    });
  }, [source, ready]);

  // Read-only panes show the machine's size; the writer's window sets it.
  useEffect(() => {
    const t = term.current;
    if (!t) return;
    t.options.disableStdin = !writable;
    // A steady cursor: nothing on screen blinks for looks (motion/perpetual), and a blink caught
    // in its off phase reads as no cursor at all.
    t.options.cursorBlink = false;
    const el = host.current;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // One type size in every pane of a layout, watched or typed into, never under 12 px: a pane
    // never shrinks its text to fit. Watching shows the machine's screen at its own size, and a
    // screen wider or taller than the pane scrolls inside it.
    if (t.options.fontSize !== baseFont) t.options.fontSize = baseFont;
    if (!writable) {
      if (t.cols !== cols || t.rows !== rows) t.resize(cols, rows);
      if (!el) return;
      // The machine's screen can be wider or taller than the pane: it scrolls inside the pane, and
      // the pane follows the newest output (the cursor's row, or the last row with text below it,
      // such as Claude Code's input box) unless you scrolled up to read.
      let pinned = true;
      let target = 0;
      let ours = false;
      const newest = () => {
        const b = t.buffer.active;
        for (let r = t.rows - 1; r > b.cursorY; r--) {
          if (b.getLine(b.viewportY + r)?.translateToString(true).trim()) return r;
        }
        return b.cursorY;
      };
      const follow = () => {
        const screen = el.querySelector<HTMLElement>('.xterm-screen');
        if (!pinned || !screen || !t.rows) return;
        const row = screen.offsetHeight / t.rows;
        const pad = el.scrollHeight - screen.offsetHeight;
        target = Math.max(0, Math.ceil((newest() + 1) * row + pad - el.clientHeight));
        if (Math.abs(el.scrollTop - target) > 1) {
          ours = true;
          el.scrollTop = target;
        }
      };
      const onScroll = () => {
        if (ours) {
          ours = false;
          return;
        }
        pinned = el.scrollTop >= target - 4;
      };
      follow();
      el.addEventListener('scroll', onScroll, { passive: true });
      const parsed = t.onWriteParsed(follow);
      const ro = new ResizeObserver(follow);
      ro.observe(el);
      const screen = el.querySelector('.xterm-screen');
      if (screen) ro.observe(screen);
      return () => {
        el.removeEventListener('scroll', onScroll);
        parsed.dispose();
        ro.disconnect();
      };
    }
    const apply = () => {
      const dims = fit.current?.proposeDimensions();
      if (!dims || !dims.cols || !dims.rows) return;
      if (dims.cols !== t.cols || dims.rows !== t.rows) t.resize(dims.cols, dims.rows);
      sourceRef.current?.resize(t.cols, t.rows);
    };
    apply();
    t.focus();
    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(apply, 120);
    });
    if (el) ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [writable, cols, rows, baseFont, ready]);

  const tone = STATE_TONE[state];
  const live = state === 'live';
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const lockText = lock ? (lock.you ? 'You have control' : `${lock.label} is typing`) : 'Read only';

  let control: ReactNode = null;
  if (writable && onHandBack) {
    control = (
      <Button size="sm" variant="secondary" icon={<LogOut />} loading={busy} onClick={onHandBack}>
        Hand back
      </Button>
    );
  } else if (!writable && live && lock && !lock.you && onForce) {
    control = (
      // One verb for one action: Take control, whether the terminal is free or someone is typing.
      <Tooltip content={`${lock.label} is typing. Taking control hands you the keyboard.`}>
        <Button size="sm" variant="outline" icon={<Hand />} loading={busy} onClick={onForce}>
          Take control
        </Button>
      </Tooltip>
    );
  } else if (!writable && live && onTakeControl) {
    const blocked = !!lock && !lock.you;
    control = (
      <Tooltip content={blocked ? `${lock!.label} is typing. Ask them to hand back.` : 'Type into this terminal. Everyone else keeps watching.'}>
        <span tabIndex={blocked ? 0 : -1}>
          {/* Side by side, every pane has one: none of them is the page's one primary action. */}
          <Button size="sm" variant={compact ? 'secondary' : 'primary'} icon={<Keyboard />} loading={busy} disabled={blocked} onClick={onTakeControl}>
            Take control
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <section
      aria-label={label}
      className={cn(
        REVEAL_GROUP,
        'flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border bg-bg',
        writable ? 'border-accent shadow-[0_0_0_1px_var(--accent-line)]' : 'border-border',
        className,
      )}
    >
      <header className={cn('flex shrink-0 items-center gap-2 border-b border-border-subtle bg-surface pl-3 pr-1.5', compact ? 'h-9' : 'h-10')}>
        <StatusDot tone={tone} size="sm" label={stateLabel(state, exitCode)} />
        <Heading className="min-w-0 flex-1 truncate text-ui font-medium text-fg" title={typeof title === 'string' ? title : undefined}>
          {title}
        </Heading>
        {/* The control sits at the header's end, after the control state. Download, end and close show
            on hover or focus, laid over the title's tail just before them, so at rest they reserve no
            width and the title takes the row, and the state label is never covered (always shown
            under touch, where they keep their own place). */}
        <div className="relative flex shrink-0 items-center gap-2">
          {actions != null && (
            <RevealSlot className="flex shrink-0 items-center gap-1 bg-surface pointer-fine:absolute pointer-fine:right-full pointer-fine:pl-2 pointer-fine:pr-1">{actions}</RevealSlot>
          )}
          {/* "Read only" is said only where nobody can take control: Take control says it otherwise. */}
          <span
            className={cn(
              'hidden shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs sm:inline-flex',
              !lock && onTakeControl && live && 'sm:hidden',
              lock ? (lock.you ? 'bg-accent-soft text-accent-text' : 'bg-waiting-soft text-waiting') : 'text-fg-subtle',
            )}
            role="status"
            aria-live="polite"
          >
            {lock ? <Keyboard className="size-3" aria-hidden /> : <Eye className="size-3" aria-hidden />}
            {lockText}
          </span>
          {control}
        </div>
      </header>

      <div className="relative min-h-0 flex-1">
        <div
          ref={host}
          className={cn(
            'absolute inset-0 overflow-auto p-1.5 [&_.xterm-viewport]:!bg-transparent',
            SCROLL_EDGE_FADE,
            // Typing, the screen is fitted to the pane; watching, it keeps the machine's size and scrolls.
            writable ? '[&_.xterm]:h-full' : '[&_.xterm]:w-max [&_.xterm]:min-h-full',
          )}
          style={{ scrollbarWidth: 'thin' }}
        />
        <span id={hintId} className="sr-only">
          {writable ? 'You have control: Tab goes to the program. Press F6 to leave the terminal for the next control, Shift F6 for the previous one.' : 'Read only. Tab moves on.'}
        </span>
        {state === 'connecting' && !gotOutput.current && (
          <div className="absolute inset-0 flex flex-col gap-2 bg-bg p-4" role="status" aria-label="Connecting to the terminal">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3.5 w-3/5" />
            <p className="mt-2 text-sm text-fg-subtle">Connecting to the terminal</p>
          </div>
        )}
        {(state === 'error' || loadFailed) && (
          <div className="absolute inset-0 flex items-center justify-center bg-bg/95">
            <ErrorState
              error={
                loadFailed
                  ? { what: 'The terminal could not load.', why: 'This page could not fetch the terminal component.', next: 'Reload the page. If it keeps failing, rebuild the console.' }
                  : error
              }
              onRetry={loadFailed ? () => window.location.reload() : onRetry}
            />
          </div>
        )}
        {state === 'disconnected' && (
          <div role="status" className="absolute inset-x-0 top-0 border-b border-offline/40 bg-offline-soft px-3 py-1.5 text-sm text-fg-muted">
            {status.machine ?? 'The machine'} is offline. Output resumes when it reconnects.
          </div>
        )}
      </div>

      <footer className="flex h-7 shrink-0 items-center gap-3 overflow-hidden border-t border-border-subtle bg-surface px-3 text-xs text-fg-subtle">
        <span className="inline-flex items-center gap-1.5 text-fg-muted">
          <StatusDot tone={tone} size="xs" label="" />
          {stateLabel(state, exitCode)}
        </span>
        <span className="truncate text-fg-muted">{status.agent}</span>
        {status.model && <span className="truncate">{status.model}</span>}
        {status.machine && <span className="hidden truncate md:inline">{status.machine}</span>}
        {status.tokens && <span className="hidden num lg:inline">{status.tokens}</span>}
        {status.cost && <span className="hidden num lg:inline">{status.cost}</span>}
        {status.backend === 'pipe' && (
          <Tooltip content="This machine has no PTY support, so full-screen programs do not work here.">
            <span tabIndex={0} className="text-waiting">
              Basic terminal
            </span>
          </Tooltip>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-3">
          {status.viewers != null && status.viewers > 1 && (
            <span className="inline-flex items-center gap-1" role="img" aria-label={`${status.viewers} people watching`}>
              <Users className="size-3" aria-hidden />
              <span className="num">{status.viewers}</span>
            </span>
          )}
          {writable && (
            <span className="hidden items-center gap-1 lg:inline-flex" aria-hidden>
              <Kbd keys={['F6']} /> to leave
            </span>
          )}
          {status.startedAt != null && <Elapsed from={status.startedAt} to={status.endedAt} />}
          <span className="hidden num sm:inline">
            {cols}x{rows}
          </span>
        </span>
      </footer>
    </section>
  );
}
